'use client';

import { defaultLocale } from '@/lib/i18n/dictionaries';
import type { GetProjects } from '@/server/api/rest/project';
import { useTranslateProjects } from '@/shared/hooks';
import { useDatabaseTranslations } from '@/shared/hooks/translations';
import { useParams } from 'next/navigation';
import type { FC } from 'react';
import React from 'react';
import { QueryWrapper } from './query-wrapper';

export const TranslatedProjects: FC<{
  projects: GetProjects;
  renderProjects: (projects: GetProjects) => React.ReactNode;
}> = ({ projects, renderProjects }) => {
  const params = useParams<{ locale?: string | string[] }>();
  const localeParam = params?.locale;
  const locale =
    typeof localeParam === 'string'
      ? localeParam
      : Array.isArray(localeParam)
        ? localeParam[0]
        : undefined;

  const projectIds = projects.map((project) => String(project.id)).join(',');

  const {
    data: translations,
    isLoading,
    error,
  } = useDatabaseTranslations({
    model: 'Project',
    fields: 'title, subtitle, challenge, solution, result',
    foreignKeys: projectIds,
    locale: locale ?? defaultLocale,
    enabled: Boolean(locale) && locale !== defaultLocale && projects.length > 0,
  });

  const {
    data: translatedProjects,
    isLoading: translatedProjectsIsLoading,
    error: translatedProjectsError,
  } = useTranslateProjects({ translations: translations ?? [], projects });

  return (
    <QueryWrapper
      isLoading={isLoading ?? translatedProjectsIsLoading}
      error={error ?? translatedProjectsError}
      data={translatedProjects}
    >
      {renderProjects}
    </QueryWrapper>
  );
};
